/**
 * Cuándo puede Softfi actualizarse solo, sin que nadie toque «Actualizar».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ HACE FALTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El aviso de versión nueva esperaba a que alguien tocara «Actualizar», y en
 * el celular Softfi casi nunca se cierra de verdad: la copia vieja seguía viva
 * días. Con varias cajeras, un arreglo de dinero —la tasa en dos decimales— no
 * llegaba a todas las cajas. Lo pidió el dueño (2026-09-25).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LOS MOMENTOS SEGUROS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · Al ABRIR Softfi, antes de que se toque nada: no hay nada que perder.
 *   · Al VOLVER después de un rato en otra aplicación, si no hay nada a medias.
 *   · Si la versión es OBLIGATORIA (la fija el panel), también cuando nadie
 *     toca la pantalla un rato.
 *
 * «Nada a medias» es: ningún cobro abierto, ninguna ventana abierta (los
 * formularios de Softfi se abren en ventanas) y nadie escribiendo en un campo.
 * Las pantallas de Ajustes y del panel son formularios en la página misma, así
 * que ahí se exige mucha más calma. El carrito de Vender no importa: queda
 * guardado aunque se recargue.
 */

import { hayCobroEnCurso } from "@/lib/cobroEnCurso";

/** Cuánto tiene que haber estado fuera para que «volver» cuente: 1 minuto. */
export const FUERA_MINIMO = 60_000;

/** Con la versión obligatoria, cuánto quieta tiene que estar la pantalla. */
export const QUIETO_PARA_OBLIGADA = 30_000;

/** Lo mismo en una pantalla que es un formulario: 5 minutos. */
export const QUIETO_EN_FORMULARIO = 5 * 60_000;

/** Las pantallas que son formularios en la página misma, no en una ventana. */
const RUTAS_CON_FORMULARIO = ["/ajustes", "/admin", "/catalogo/importar"];

export function rutaConFormulario(ruta: string): boolean {
  return RUTAS_CON_FORMULARIO.some((r) => ruta === r || ruta.startsWith(`${r}/`));
}

/** ¿Se puede recargar sin llevarse nada por delante? */
export function esMomentoSeguro(doc: Document = document): boolean {
  if (hayCobroEnCurso()) return false;
  if (doc.querySelector('[role="dialog"], [aria-modal="true"]')) return false;
  const activo = doc.activeElement;
  if (activo?.matches('input, textarea, select, [contenteditable="true"]')) return false;
  return true;
}

/**
 * ¿Esta copia es más vieja que la que exige el panel?
 *
 * Las dos son fechas de compilación en el mismo formato (`2026-09-25T20:15:03.123Z`),
 * así que compararlas como texto es compararlas como fechas.
 */
export function esMasViejaQue(mia: string, minima: string | null | undefined): boolean {
  return Boolean(minima) && mia < (minima as string);
}

// =============================================================================
//  ⚠️ QUE NUNCA SE QUEDE RECARGANDO EN BUCLE
// =============================================================================
//
// Si la actualización no llega —el service worker se atascó, el servidor sirve
// una copia vieja— recargar otra vez daría lo mismo, y otra, y otra: la caja
// quedaría inservible. Así que cada intento se anota, y a la segunda vuelta
// fallida hacia la misma versión se deja de intentar solo. Queda el aviso con
// su botón, que tiene su propio último recurso (`traerLaNueva`).

const LLAVE = "softfi-actualizacion-sola";
const VENTANA = 10 * 60_000;
const INTENTOS = 2;

type Intento = { hacia: string; veces: number; desde: number };

function leer(): Intento | null {
  try {
    const texto = sessionStorage.getItem(LLAVE);
    return texto ? (JSON.parse(texto) as Intento) : null;
  } catch {
    return null;
  }
}

/** ¿Se pueden anotar los intentos? En una ventana privada puede que no. */
function hayDondeAnotar(): boolean {
  try {
    sessionStorage.setItem(`${LLAVE}-prueba`, "1");
    sessionStorage.removeItem(`${LLAVE}-prueba`);
    return true;
  } catch {
    return false;
  }
}

export function puedoIntentar(hacia: string, ahora = Date.now()): boolean {
  // Sin dónde anotar no se sabría cuántas vueltas lleva: no se intenta solo.
  if (!hayDondeAnotar()) return false;
  const antes = leer();
  if (!antes || antes.hacia !== hacia || ahora - antes.desde > VENTANA) return true;
  return antes.veces < INTENTOS;
}

export function anotarIntento(hacia: string, ahora = Date.now()): void {
  const antes = leer();
  const sigue = antes && antes.hacia === hacia && ahora - antes.desde <= VENTANA;
  const nuevo: Intento = sigue
    ? { ...antes, veces: antes.veces + 1 }
    : { hacia, veces: 1, desde: ahora };
  try {
    sessionStorage.setItem(LLAVE, JSON.stringify(nuevo));
  } catch {
    // No pasa: `puedoIntentar` ya comprobó que se puede anotar.
  }
}

/** Para las pruebas. */
export function olvidarIntentos(): void {
  try {
    sessionStorage.removeItem(LLAVE);
  } catch {
    // nada
  }
}
